/**
 * PATRON POINTS — the Command Center section. Stage 1 is READ ONLY: nothing on
 * any tab here sends, spends or edits anything.
 *
 * Home, Players and the player profile are new. Send and Rewards are labelled
 * and disabled. Design opens the existing fan-page editor and Settings holds
 * the existing set-up cards, both unchanged — this page only frames them.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Lock } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import api from '../../lib/api';
import LanguageToggle from '../../components/LanguageToggle';
import { TimeRangePicker, buildPreset } from '../../components/analytics/TimeRangePicker';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import PatronPagesBlock from '../../components/patron/PatronPagesBlock';
import { PatronDemoSeedBlock } from '../../components/patron/PatronDemoSeedBlock';
import { PatronHome } from '../../components/patron/insights/PatronHome';
import { PatronPlayers } from '../../components/patron/insights/PatronPlayers';
import { PatronProfile } from '../../components/patron/insights/PatronProfile';
import PatronPoints from './PatronPoints';

const TABS = ['home', 'players', 'send', 'rewards', 'design', 'settings'];
const DISABLED = ['send', 'rewards'];

/** No tick, no page. A direct link lands here instead of a blank screen; the
 *  tick itself is in the permissions screen's "Goal Game" group. */
const NoGoalAccess = () => {
    const { t } = useTranslation();
    return (
        <div className="p-8 text-center" data-testid="goal-no-access">
            <Lock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-lg font-medium">{t('goalAccess.title')}</p>
            <p className="text-sm text-muted-foreground">{t('goalAccess.body')}</p>
        </div>
    );
};

function PatronCommandPage() {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [tab, setTab] = useState('home');
    const [fanId, setFanId] = useState(null);
    const [range, setRange] = useState({ ...buildPreset('last30d'), labelKey: 'last30d' });
    const [demo, setDemo] = useState(null);
    const [stamp, setStamp] = useState(0);

    useEffect(() => {
        if (!orgId) return;
        (async () => {
            try {
                const res = await api.get('/patron/demo-players/status',
                    { params: { organization_id: orgId } });
                setDemo(res.data);
            } catch (_e) { setDemo(null); }
        })();
    }, [orgId, stamp]);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/organizations');
                const rows = (res.data?.organizations || res.data || [])
                    .filter((o) => o.goal_game_enabled);
                setOrgs(rows);
                if (rows.length) setOrgId(rows[0].id);
            } catch (_e) { toast.error('Could not load the venues'); }
        })();
    }, []);

    const openPlayer = (id) => { setFanId(id); setTab('players'); };

    return (
        <div className="p-4 sm:p-6 space-y-5" data-testid="patron-command">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold grow" data-testid="patron-command-title">
                    <Coins size={20} className="inline mr-2 -mt-1" />{t('patronCommand.title')}
                    {demo?.seeded && (
                        <Badge variant="secondary" className="ml-3 align-middle text-[10px]"
                               data-testid="patron-demo-pill">
                            {t('patronCommand.demo.pill')}
                        </Badge>
                    )}
                </h1>
                <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setFanId(null); }}
                        data-testid="patron-command-org"
                        className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm">
                    {orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.display_name || o.name}</option>
                    ))}
                </select>
                {tab === 'home' && <TimeRangePicker value={range} onChange={setRange} />}
                <LanguageToggle variant="compact-inline" surface="patron-command" />
            </div>
            <p className="text-xs text-slate-500" data-testid="patron-command-readonly">
                {t('patronCommand.readOnly')}
            </p>

            <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700"
                 data-testid="patron-command-tabs">
                {TABS.map((key) => {
                    const off = DISABLED.includes(key);
                    const on = tab === key;
                    return (
                        <button key={key} disabled={off}
                                onClick={() => { setTab(key); setFanId(null); }}
                                data-testid={`patron-tab-${key}`}
                                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                    on ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                       : off ? 'border-transparent text-slate-400 cursor-not-allowed'
                                       : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
                            {off && <Lock size={12} />}
                            {t(`patronCommand.tabs.${key}`)}
                            {off && (
                                <Badge variant="secondary" className="text-[9px] ml-1"
                                       data-testid={`patron-tab-${key}-soon`}>
                                    {t('patronCommand.comingNext')}
                                </Badge>
                            )}
                        </button>
                    );
                })}
            </div>

            {!orgId ? null : tab === 'home' ? (
                <PatronHome orgId={orgId} range={range} onOpenPlayer={openPlayer} />
            ) : tab === 'players' ? (
                fanId ? (
                    <PatronProfile orgId={orgId} fanId={fanId} onBack={() => setFanId(null)} />
                ) : (
                    <PatronPlayers orgId={orgId} onOpenPlayer={openPlayer} />
                )
            ) : tab === 'design' ? (
                <Card data-testid="patron-tab-design-panel">
                    <CardContent className="pt-5"><PatronPagesBlock orgId={orgId} /></CardContent>
                </Card>
            ) : tab === 'settings' ? (
                <div data-testid="patron-tab-settings-panel" className="space-y-4">
                    <div className="px-4 sm:px-6">
                        <PatronDemoSeedBlock orgId={orgId} status={demo}
                                             onChanged={() => setStamp((n) => n + 1)} />
                    </div>
                    <p className="text-xs text-slate-500 px-4 sm:px-6">
                        {t('patronCommand.settingsNote')}
                    </p>
                    <PatronPoints />
                </div>
            ) : null}
        </div>
    );
}

export default function PatronCommand() {
    const { hasPermission } = useAuth();
    if (!hasPermission('manage_patron_points')) return <NoGoalAccess />;
    return <PatronCommandPage />;
}
